/**
 * Qué puede ver cada usuario y adónde llega al entrar.
 *
 * `SEG-1`: esto **oculta y redirige; no autoriza**. La autorización vive en el
 * backend, en cada caso de uso. Lo de aquí evita ofrecerle a alguien una
 * pantalla que le va a devolver 403 — que es cortesía, no seguridad.
 *
 * `ARQ-10`: se razona por PERMISO, nunca por rol. En este archivo no aparece
 * `rol === 'analista'`, y no debe aparecer: el administrador puede crear un
 * rol nuevo con cualquier reparto sin tocar código.
 */
import type { UsuarioSesion } from '../api/tipos.js'
import { RUTAS, type DefinicionRuta, type Vista } from './rutas.js'

export function tienePermiso(usuario: UsuarioSesion, ...permisos: readonly string[]): boolean {
  return permisos.some((permiso) => usuario.permisos.includes(permiso))
}

/** Una ruta sin permisos declarados solo exige sesión. */
export function rutaPermitida(usuario: UsuarioSesion, ruta: DefinicionRuta): boolean {
  if (ruta.permisos.length === 0) return true
  return tienePermiso(usuario, ...ruta.permisos)
}

export function rutasVisibles(usuario: UsuarioSesion): readonly DefinicionRuta[] {
  return RUTAS.filter((ruta) => ruta.grupo !== undefined && rutaPermitida(usuario, ruta))
}

/**
 * Adónde va alguien al iniciar sesión.
 *
 * El orden importa y no es alfabético: es el de «qué hace esta persona todo el
 * día». Un notificador entra a su bandeja, no a un panel que no puede leer;
 * Mesa de Partes entra a su cola. El analista y el coordinador van al panel.
 *
 * Si no encaja en ninguno —un rol nuevo con permisos solo de administración—
 * se toma la primera ruta visible, y si no hay ninguna, la de acceso: una
 * cuenta sin ningún permiso no tiene ninguna pantalla que mostrar.
 */
const PRIORIDAD: ReadonlyArray<{ vista: Vista; permiso: string }> = [
  { vista: 'panel', permiso: 'expediente.ver' },
  { vista: 'mesaPartes', permiso: 'consulta.paso.mesa_partes' },
  { vista: 'misOficios', permiso: 'consulta.paso.notificador' },
  { vista: 'sorteo', permiso: 'sorteo.ejecutar' },
  { vista: 'usuarios', permiso: 'usuario.administrar' },
  { vista: 'auditoria', permiso: 'auditoria.ver' },
]

export function vistaInicial(usuario: UsuarioSesion): Vista {
  if (usuario.requiereCambioPassword) return 'primerAcceso'

  const preferida = PRIORIDAD.find((opcion) => tienePermiso(usuario, opcion.permiso))
  if (preferida) return preferida.vista

  return rutasVisibles(usuario)[0]?.vista ?? 'acceso'
}
